// src/pages/EDITORIAL/ECompany.tsx
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import { getDownloadURL, getStorage, ref as storageRef, uploadBytes } from "firebase/storage";
import { upsertCompany, upsertCompanyMedia, fetchCompanies, deleteCompany } from "../redux/companiesSlice";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle, AlertCircle, Loader2, X, Camera, Plus, Trash2, ChevronDown,
} from "lucide-react";

/* ─── Font ─── */
const FONT_CSS = `@import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600;700&display=swap');`;

/* ─── Tokens ─── */
const C = {
  white:  "#FFFFFF",
  off:    "#F8F7F5",
  black:  "#0A0A0A",
  gray:   "#6B6B6B",
  grayLt: "#D4D4D4",
  border: "#E5E3DF",
  red:    "#E50914",
  redLt:  "#FFF0F0",
  sage:   "#4A7C59",
  sageLt: "#F0F6F2",
  serif:  "'DM Serif Display', serif",
  sans:   "'DM Sans', sans-serif",
};

const INDUSTRIES = [
  "Technology", "Luxury & Real Estate", "Fashion & Beauty",
  "Electronics", "Media & Entertainment", "Finance",
  "Identity & Security", "Travel & Experiences", "Other",
];

/* ═══════════════════════════════════════
   ECOMPANY — Admin company editor
═══════════════════════════════════════ */
const ECompany: React.FC = () => {
  const dispatch  = useDispatch<AppDispatch>();
  const companies = useSelector((s: RootState) => s.companies.companies);

  /* ── Form ── */
  const [id,          setId]          = useState("");
  const [name,        setName]        = useState("");
  const [slug,        setSlug]        = useState("");
  const [slugEdited,  setSlugEdited]  = useState(false);
  const [tagline,     setTagline]     = useState("");
  const [description, setDescription] = useState("");
  const [industry,    setIndustry]    = useState("");
  const [website,     setWebsite]     = useState("");
  const [founded,     setFounded]     = useState("");
  const [location,    setLocation]    = useState("");
  const [logoUrl,     setLogoUrl]     = useState("");
  const [coverImage,  setCoverImage]  = useState("");
  const [mediaUrls,   setMediaUrls]   = useState<string[]>([]);
  const [published,   setPublished]   = useState(false);

  /* ── UI ── */
  const [loading,   setLoading]   = useState(false);
  const [success,   setSuccess]   = useState(false);
  const [error,     setError]     = useState<string | null>(null);
  const [tab,       setTab]       = useState<"editor" | "list">("editor");
  const [uploading, setUploading] = useState(false);
  const [focus,     setFocus]     = useState<string | null>(null);

  const coverRef = useRef<HTMLInputElement>(null);
  const logoRef  = useRef<HTMLInputElement>(null);
  const mediaRef = useRef<HTMLInputElement>(null);

  useEffect(() => { dispatch(fetchCompanies()); }, [dispatch]);

  /* ── Helpers ── */
  const genSlug = (t: string) => t.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

  const handleNameChange = (val: string) => {
    setName(val);
    if (!slugEdited) setSlug(genSlug(val));
  };

  const inp = (field: string): React.CSSProperties => ({
    width: "100%", boxSizing: "border-box" as const,
    background: C.white, border: `1.5px solid ${focus === field ? C.red : C.border}`,
    padding: "12px 14px", fontSize: 13, color: C.black,
    fontFamily: C.sans, outline: "none", transition: "border-color 0.2s",
  });

  const sel: React.CSSProperties = {
    ...inp(""), appearance: "none" as const, cursor: "pointer", paddingRight: 36,
  };

  /* ── Upload helpers ── */
  const uploadFile = async (file: File, path: string): Promise<string> => {
    const storage = getStorage();
    const r       = storageRef(storage, path);
    await uploadBytes(r, file);
    return getDownloadURL(r);
  };

  const uploadLogo = async (file: File) => {
    setUploading(true);
    try { setLogoUrl(await uploadFile(file, `companies/logo-${Date.now()}.${file.name.split(".").pop()}`)); }
    catch { setError("Logo upload failed."); }
    setUploading(false);
  };

  const uploadCover = async (file: File) => {
    setUploading(true);
    try { setCoverImage(await uploadFile(file, `companies/cover-${Date.now()}.${file.name.split(".").pop()}`)); }
    catch { setError("Cover upload failed."); }
    setUploading(false);
  };

  const uploadMedia = async (files: FileList) => {
    setUploading(true);
    const storage = getStorage();
    const urls: string[] = [];
    for (const file of Array.from(files)) {
      const r = storageRef(storage, `companies/media-${Date.now()}-${file.name}`);
      await uploadBytes(r, file);
      urls.push(await getDownloadURL(r));
    }
    const next = [...mediaUrls, ...urls];
    setMediaUrls(next);
    if (id) await dispatch(upsertCompanyMedia({ id, mediaUrls: next }));
    setUploading(false);
  };

  /* ── Reset ── */
  const reset = () => {
    setId(""); setName(""); setSlug(""); setSlugEdited(false);
    setTagline(""); setDescription(""); setIndustry("");
    setWebsite(""); setFounded(""); setLocation("");
    setLogoUrl(""); setCoverImage(""); setMediaUrls([]); setPublished(false);
  };

  /* ── Load company ── */
  const loadCompany = (co: any) => {
    setId(co.id); setName(co.name); setSlug(co.slug); setSlugEdited(true);
    setTagline(co.tagline || ""); setDescription(co.description || "");
    setIndustry(co.industry || ""); setWebsite(co.website || "");
    setFounded(co.founded || ""); setLocation(co.location || "");
    setLogoUrl(co.logoUrl || ""); setCoverImage(co.coverImage || "");
    setMediaUrls(co.mediaUrls || []); setPublished(co.published || false);
    setTab("editor");
  };

  /* ── Submit ── */
  const handleSubmit = async (pub: boolean) => {
    if (!name.trim())        { setError("Company name is required."); return; }
    if (!description.trim()) { setError("Description is required."); return; }
    setLoading(true); setError(null);
    try {
      await dispatch(upsertCompany({
        id, name, slug: slug || genSlug(name),
        tagline, description, industry, website,
        founded, location, logoUrl, coverImage,
        mediaUrls, published: pub,
      })).unwrap();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      reset();
    } catch (e: any) {
      setError(e.message || "Failed to save.");
    }
    setLoading(false);
  };

  /* ── Delete ── */
  const handleDelete = async (cId: string) => {
    if (!window.confirm("Delete this company?")) return;
    await dispatch(deleteCompany(cId));
  };

  /* ════════════════ RENDER ════════════════ */
  return (
    <div style={{ background: C.off, fontFamily: C.sans, minHeight: "100vh" }}>
      <style>{FONT_CSS + CSS}</style>

      {/* ── Strip ── */}
      <div style={{ background: C.white, borderBottom: `1px solid ${C.border}`, padding: "20px 28px" }}>
        <p style={{ fontSize: 10, letterSpacing: "0.3em", color: C.red, fontWeight: 700, textTransform: "uppercase", margin: "0 0 4px" }}>EDITORIAL</p>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <h1 style={{ fontFamily: C.serif, fontSize: "clamp(22px,3vw,34px)", fontWeight: 400, color: C.black, margin: 0 }}>Companies</h1>
          <div style={{ display: "flex", gap: 8 }}>
            <TabBtn active={tab === "editor"} onClick={() => setTab("editor")}>Editor</TabBtn>
            <TabBtn active={tab === "list"}   onClick={() => setTab("list")}>
              All Companies ({companies.length})
            </TabBtn>
          </div>
        </div>
      </div>

      {/* ── Toast ── */}
      <AnimatePresence>
        {(success || error) && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            style={{ display: "flex", alignItems: "center", gap: 10, margin: "16px 28px 0", padding: "13px 16px",
              background: success ? C.sageLt : C.redLt,
              border: `1px solid ${success ? C.sage : C.red}30`,
            }}>
            {success ? <CheckCircle size={15} color={C.sage} /> : <AlertCircle size={15} color={C.red} />}
            <span style={{ fontSize: 13, color: success ? C.sage : C.red, flex: 1 }}>
              {success ? "Company saved successfully!" : error}
            </span>
            {error && <button onClick={() => setError(null)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={13} color={C.gray} /></button>}
          </motion.div>
        )}
      </AnimatePresence>

      <div style={{ padding: "24px 28px 60px" }}>

        {/* ════════ EDITOR TAB ════════ */}
        {tab === "editor" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 24, alignItems: "start" }} className="eco-layout">

            {/* Left */}
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

              <Card title="Company Identity">
                <Lbl required>Company Name</Lbl>
                <input value={name} onChange={e => handleNameChange(e.target.value)}
                  onFocus={() => setFocus("name")} onBlur={() => setFocus(null)}
                  placeholder="e.g. AINTEQ"
                  style={{ ...inp("name"), fontSize: 18, fontFamily: C.serif }} />

                <div>
                  <Lbl>Tagline</Lbl>
                  <input value={tagline} onChange={e => setTagline(e.target.value)}
                    onFocus={() => setFocus("tag")} onBlur={() => setFocus(null)}
                    placeholder="A short memorable tagline" style={inp("tag")} />
                </div>

                <div>
                  <Lbl>URL Slug</Lbl>
                  <div style={{ display: "flex", border: `1.5px solid ${C.border}`, background: C.white }}>
                    <span style={{ fontSize: 11, color: C.gray, padding: "0 12px", lineHeight: "42px", borderRight: `1px solid ${C.border}`, flexShrink: 0 }}>
                      4reva.com/companies/
                    </span>
                    <input value={slug} onChange={e => { setSlug(genSlug(e.target.value)); setSlugEdited(true); }}
                      style={{ flex: 1, border: "none", outline: "none", padding: "11px 12px", fontSize: 12, fontFamily: "'Courier New', monospace", color: C.black, background: "transparent" }} />
                    {slugEdited && (
                      <button onClick={() => { setSlug(genSlug(name)); setSlugEdited(false); }}
                        style={{ fontSize: 9, color: C.gray, background: "none", border: "none", cursor: "pointer", padding: "0 12px", letterSpacing: "0.1em" }}>
                        RESET
                      </button>
                    )}
                  </div>
                </div>
              </Card>

              <Card title="Company Details">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div>
                    <Lbl>Industry</Lbl>
                    <div style={{ position: "relative" }}>
                      <select value={industry} onChange={e => setIndustry(e.target.value)} style={sel}>
                        <option value="">— Select —</option>
                        {INDUSTRIES.map(i => <option key={i}>{i}</option>)}
                      </select>
                      <ChevronDown size={13} color={C.gray} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
                    </div>
                  </div>
                  <div>
                    <Lbl>Year Founded</Lbl>
                    <input value={founded} onChange={e => setFounded(e.target.value)}
                      onFocus={() => setFocus("founded")} onBlur={() => setFocus(null)}
                      placeholder="e.g. 2019" style={inp("founded")} />
                  </div>
                  <div>
                    <Lbl>Headquarters</Lbl>
                    <input value={location} onChange={e => setLocation(e.target.value)}
                      onFocus={() => setFocus("loc")} onBlur={() => setFocus(null)}
                      placeholder="Tallinn, Estonia" style={inp("loc")} />
                  </div>
                  <div>
                    <Lbl>Website</Lbl>
                    <input value={website} onChange={e => setWebsite(e.target.value)}
                      onFocus={() => setFocus("web")} onBlur={() => setFocus(null)}
                      placeholder="https://ainteq.com" style={inp("web")} />
                  </div>
                </div>
              </Card>

              <Card title="About">
                <Lbl required>Description</Lbl>
                <textarea value={description} onChange={e => setDescription(e.target.value)}
                  onFocus={() => setFocus("desc")} onBlur={() => setFocus(null)}
                  placeholder="Describe the company — its mission, products, and impact…"
                  rows={8} style={{ ...inp("desc"), resize: "vertical" as const, lineHeight: 1.75 }} />
              </Card>

              {/* Media gallery */}
              <Card title="Media Gallery">
                <Lbl>Images & Videos</Lbl>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 8, marginBottom: 12 }}>
                  {mediaUrls.map((url, i) => (
                    <div key={i} style={{ position: "relative", aspectRatio: "1", overflow: "hidden", border: `1px solid ${C.border}` }}>
                      <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      <button onClick={() => setMediaUrls(mediaUrls.filter((_, j) => j !== i))}
                        style={{ position: "absolute", top: 4, right: 4, background: "rgba(0,0,0,0.6)", border: "none", borderRadius: "50%", width: 22, height: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <X size={11} color="#fff" />
                      </button>
                    </div>
                  ))}
                  <label style={{ aspectRatio: "1", border: `2px dashed ${C.border}`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", background: C.off, gap: 6 }}>
                    <Plus size={20} color={C.gray} />
                    <span style={{ fontSize: 10, color: C.gray, letterSpacing: "0.1em" }}>ADD</span>
                    <input ref={mediaRef} type="file" accept="image/*,video/*" multiple hidden
                      onChange={e => e.target.files && uploadMedia(e.target.files)} />
                  </label>
                </div>
                {uploading && <p style={{ fontSize: 11, color: C.gray, margin: 0 }}>Uploading…</p>}
              </Card>

              {/* Actions */}
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <Btn onClick={() => handleSubmit(true)} loading={loading} primary>
                  {id ? "UPDATE & PUBLISH" : "PUBLISH COMPANY"}
                </Btn>
                <Btn onClick={() => handleSubmit(false)} loading={loading}>
                  SAVE DRAFT
                </Btn>
                {id && <Btn onClick={reset} secondary>NEW COMPANY</Btn>}
              </div>
            </div>

            {/* Right sidebar */}
            <div style={{ display: "flex", flexDirection: "column", gap: 16, position: "sticky" as const, top: 20 }}>

              {/* Logo */}
              <Card title="Company Logo">
                {logoUrl ? (
                  <div style={{ position: "relative" }}>
                    <img src={logoUrl} alt="Logo" style={{ width: "100%", maxHeight: 120, objectFit: "contain", display: "block", background: C.off, padding: 12 }} />
                    <button onClick={() => setLogoUrl("")}
                      style={{ position: "absolute", top: 8, right: 8, background: "rgba(0,0,0,0.6)", border: "none", borderRadius: "50%", width: 26, height: 26, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <X size={13} color="#fff" />
                    </button>
                  </div>
                ) : (
                  <label style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", border: `2px dashed ${C.border}`, padding: "24px 16px", cursor: "pointer", background: C.off, gap: 8 }}>
                    <Camera size={20} color={C.grayLt} strokeWidth={1.5} />
                    <span style={{ fontSize: 11, color: C.gray }}>Upload logo</span>
                    <input ref={logoRef} type="file" accept="image/*" hidden onChange={e => e.target.files?.[0] && uploadLogo(e.target.files[0])} />
                  </label>
                )}
              </Card>

              {/* Cover */}
              <Card title="Cover Image">
                {coverImage ? (
                  <div style={{ position: "relative" }}>
                    <img src={coverImage} alt="Cover" style={{ width: "100%", maxHeight: 160, objectFit: "cover", display: "block" }} />
                    <button onClick={() => setCoverImage("")}
                      style={{ position: "absolute", top: 8, right: 8, background: "rgba(0,0,0,0.6)", border: "none", borderRadius: "50%", width: 26, height: 26, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <X size={13} color="#fff" />
                    </button>
                  </div>
                ) : (
                  <label style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", border: `2px dashed ${C.border}`, padding: "24px 16px", cursor: "pointer", background: C.off, gap: 8 }}>
                    <Camera size={20} color={C.grayLt} strokeWidth={1.5} />
                    <span style={{ fontSize: 11, color: C.gray }}>Upload cover</span>
                    <input ref={coverRef} type="file" accept="image/*" hidden onChange={e => e.target.files?.[0] && uploadCover(e.target.files[0])} />
                  </label>
                )}
              </Card>

              {/* Status */}
              <Card title="Status">
                <div onClick={() => setPublished(p => !p)} style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
                  <div style={{ width: 40, height: 22, borderRadius: 999, background: published ? C.red : C.border, position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
                    <div style={{ position: "absolute", top: 3, left: published ? 20 : 3, width: 16, height: 16, borderRadius: "50%", background: C.white, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 600, color: published ? C.red : C.gray }}>
                    {published ? "Published" : "Draft"}
                  </span>
                </div>
              </Card>

              {/* Checklist */}
              <Card title="Checklist">
                {[
                  { label: "Name",        done: !!name.trim() },
                  { label: "Tagline",     done: !!tagline.trim() },
                  { label: "Industry",    done: !!industry },
                  { label: "Description", done: description.trim().length > 20 },
                  { label: "Logo",        done: !!logoUrl },
                  { label: "Cover image", done: !!coverImage },
                  { label: "Website",     done: !!website.trim() },
                ].map(item => (
                  <div key={item.label} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    <div style={{ width: 16, height: 16, borderRadius: "50%", flexShrink: 0, background: item.done ? C.black : "transparent", border: `1.5px solid ${item.done ? C.black : C.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {item.done && <div style={{ width: 5, height: 5, borderRadius: "50%", background: C.white }} />}
                    </div>
                    <span style={{ fontSize: 11, color: item.done ? C.black : C.gray }}>{item.label}</span>
                  </div>
                ))}
              </Card>
            </div>
          </div>
        )}

        {/* ════════ LIST TAB ════════ */}
        {tab === "list" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {companies.length === 0 && (
              <div style={{ textAlign: "center", padding: "60px 20px", color: C.gray, fontSize: 14 }}>
                No companies yet. Create one in the Editor tab.
              </div>
            )}
            {companies.map(co => (
              <div key={co.id} style={{ background: C.white, border: `1px solid ${C.border}`, padding: "18px 20px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                {co.logoUrl && <img src={co.logoUrl} alt="" style={{ width: 48, height: 48, objectFit: "contain", flexShrink: 0, border: `1px solid ${C.border}`, background: C.off, padding: 4 }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: C.black }}>{co.name}</span>
                    <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.16em", color: co.published ? C.sage : C.gray, background: co.published ? C.sageLt : C.off, padding: "2px 10px", textTransform: "uppercase" }}>
                      {co.published ? "Published" : "Draft"}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: C.gray }}>{co.tagline || co.description?.slice(0, 80)}</div>
                  <div style={{ fontSize: 10, color: C.grayLt, marginTop: 4 }}>{co.industry} · {co.location} · {co.website}</div>
                </div>
                <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                  <motion.button whileHover={{ scale: 1.03 }} onClick={() => loadCompany(co)}
                    style={{ background: C.black, color: C.white, border: "none", padding: "9px 18px", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", cursor: "pointer" }}>
                    EDIT
                  </motion.button>
                  <motion.button whileHover={{ scale: 1.03 }} onClick={() => handleDelete(co.id)}
                    style={{ background: "transparent", color: C.red, border: `1px solid ${C.red}`, padding: "9px 14px", fontSize: 11, cursor: "pointer", display: "flex", alignItems: "center" }}>
                    <Trash2 size={13} />
                  </motion.button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ECompany;

/* ─── Sub-components ─── */
function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: "#FFFFFF", border: `1px solid ${C.border}`, padding: "22px 20px" }}>
      <p style={{ fontSize: 9, letterSpacing: "0.25em", color: C.red, fontWeight: 700, textTransform: "uppercase", margin: "0 0 18px" }}>{title}</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>{children}</div>
    </div>
  );
}

function Lbl({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <label style={{ fontSize: 10, letterSpacing: "0.14em", color: "#444", fontWeight: 700, textTransform: "uppercase", display: "block", marginBottom: 6, fontFamily: C.sans }}>
      {children}{required && <span style={{ color: C.red, marginLeft: 3 }}>*</span>}
    </label>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      style={{ background: active ? C.black : "transparent", color: active ? C.white : C.gray, border: `1px solid ${active ? C.black : C.border}`, padding: "8px 18px", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", cursor: "pointer", fontFamily: C.sans, transition: "all 0.2s" }}>
      {children}
    </button>
  );
}

function Btn({ children, onClick, loading, primary, secondary }: { children: React.ReactNode; onClick: () => void; loading?: boolean; primary?: boolean; secondary?: boolean }) {
  return (
    <motion.button whileHover={{ scale: loading ? 1 : 1.01 }} whileTap={{ scale: loading ? 1 : 0.98 }}
      onClick={onClick} disabled={loading}
      style={{ background: secondary ? "transparent" : primary ? C.red : C.black, color: secondary ? C.black : C.white, border: secondary ? `1px solid ${C.border}` : "none", padding: "13px 28px", fontWeight: 700, fontSize: 11, letterSpacing: "0.14em", cursor: loading ? "wait" : "pointer", fontFamily: C.sans, display: "flex", alignItems: "center", gap: 8, opacity: loading ? 0.6 : 1, transition: "all 0.2s" }}>
      {loading && <Loader2 size={13} className="eco-spin" />}
      {children}
    </motion.button>
  );
}

const CSS = `
  @keyframes eco-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  .eco-spin { animation: eco-spin 1s linear infinite; }
  @media (max-width: 768px) {
    .eco-layout { grid-template-columns: 1fr !important; }
  }
`;